import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Camera, CheckCircle2, Send, X } from 'lucide-react';
import { Button } from '../ui/Button';
import type { PropsCaptura } from './registro';
import { CampoDeclarativo } from './CapturaTexto';
import { subirMedia } from '../../lib/media';
import { sesionKeys } from '../../lib/sesion';
import type { ApiError } from '../../lib/api';

/**
 * Captura de la primitiva `foto` COMO CAPTURA PRINCIPAL (Oleada 3 · lote
 * «foto como captura principal»). La foto de la maqueta y sus campos (título,
 * propuesta de valor…) viajan JUNTOS en un multipart y quedan como UN aporte
 * citable (ADR-0005 §5): la cita ya no se rompe entre la imagen y su texto.
 *
 * A diferencia de las demás primitivas, no usa `onEnviar` (que postea JSON):
 * sube directo por el endpoint de media con su propia mutación e invalida las
 * mismas cachés que el flujo normal («Llevás N aportes» se actualiza igual).
 */
export function CapturaFoto({ captura, deshabilitado, enviando, actividadId }: PropsCaptura) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [valores, setValores] = useState<Record<string, string | number>>({});
  const [subida, setSubida] = useState(false);

  // Object URL de la vista previa, revocado al cambiar/desmontar.
  useEffect(() => {
    if (!archivo) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(archivo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [archivo]);

  const mut = useMutation({
    mutationFn: (f: File) =>
      subirMedia(
        actividadId!,
        f,
        'foto',
        { filename: f.name || 'foto.jpg', mime: f.type || 'image/jpeg' },
        undefined,
        { campos: valores },
      ),
    onSuccess: () => {
      setSubida(true);
      setArchivo(null);
      setValores({});
      if (inputRef.current) inputRef.current.value = '';
      void qc.invalidateQueries({ queryKey: ['aportes', actividadId] });
      if (actividadId) {
        void qc.invalidateQueries({ queryKey: sesionKeys.actividad(actividadId) });
      }
    },
  });

  const nombres = Object.keys(captura.campos);
  const faltantes: string[] = [];
  if (!archivo) faltantes.push('la foto');
  for (const nombre of nombres) {
    const campo = captura.campos[nombre];
    if (campo.requerido === false) continue;
    const v = valores[nombre];
    const lleno = typeof v === 'number' || (typeof v === 'string' && v.trim().length > 0);
    if (!lleno) {
      faltantes.push(campo.etiqueta ?? nombre.charAt(0).toUpperCase() + nombre.slice(1));
    }
  }
  const completo = faltantes.length === 0;
  const ocupado = enviando || mut.isPending || deshabilitado || !actividadId;
  const error = (mut.error as ApiError | null)?.detail ?? null;

  return (
    <div className="flex flex-col gap-4">
      {preview ? (
        <div className="relative overflow-hidden rounded-lg border border-border-strong">
          <img src={preview} alt="Tu foto, lista para enviar" className="max-h-64 w-full object-contain bg-surface-sunken" />
          <button
            type="button"
            onClick={() => setArchivo(null)}
            disabled={ocupado}
            aria-label="Quitar la foto"
            className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full bg-surface-raised/90 text-content shadow-raised focus-visible:outline-none focus-visible:shadow-focus"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={ocupado}
          className="flex min-h-touch flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border-strong bg-surface-sunken px-4 py-8 font-sans text-sm font-medium text-content transition-colors duration-fast hover:border-primary focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-50"
        >
          <Camera className="h-8 w-8 text-primary" aria-hidden="true" />
          Tocá para tomar o elegir la foto
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
      />

      {nombres.map((nombre) => (
        <CampoDeclarativo
          key={nombre}
          nombre={nombre}
          campo={captura.campos[nombre]}
          valor={valores[nombre]}
          onCambiar={(v) =>
            setValores((prev) => {
              if (v === undefined) {
                const { [nombre]: _omitido, ...resto } = prev;
                return resto;
              }
              return { ...prev, [nombre]: v };
            })
          }
          deshabilitado={ocupado}
        />
      ))}

      <Button
        variant="primary"
        fullWidth
        loading={mut.isPending}
        disabled={!completo || ocupado}
        onClick={() => archivo && mut.mutate(archivo)}
        leadingIcon={<Send className="h-5 w-5" aria-hidden="true" />}
      >
        Enviar foto con su ficha
      </Button>
      {!completo && (
        <p className="font-sans text-sm text-content-muted" aria-live="polite">
          Falta: {faltantes.join(', ')}
        </p>
      )}
      {subida && !mut.isPending && (
        <p className="flex items-center gap-1.5 font-sans text-sm text-success" role="status">
          <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          Tu foto quedó en el acta con su ficha. Podés subir otra.
        </p>
      )}
      {error && (
        <p className="flex items-center gap-1.5 font-sans text-sm text-error" role="alert">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}
